/* eslint-disable no-console */
import React, { Fragment, useEffect } from 'react';
import { DatePicker, Select, Upload } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faChevronUp } from '@fortawesome/free-solid-svg-icons';
import PropTypes from 'prop-types';
import dayjs from 'dayjs';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import H3Text from '@Uilib/h3Text';
import H3FormInput from '@Uilib/h3FormInput';
import {
  INFINITE_EXPIRY_DATE, QUANTITY, DEFAULT_CUR_ROUND_OFF,
} from '@Apis/constants';
import './style.scss';
import WarehouseLocationSelector from '../../../../Common/Selector/WarehouseLocationSelector';
import H3CustomInput from '../../../../../uilib/h3CustomInput';
import BatchCustomFields from '../../../../Common/BatchCustomFields';
import CustomFieldLine from '../../../../Common/CustomFieldLine';

const BatchSelectorForFG = ({
  formSubmitted, org, isBulkUploaded, batchNumber, setBatchNumber, disabled, isBatchNumberEditable, lotNumber, setLotNumber, setArNumber,
  arNumber, fgBatchCostPrice, setFgBatchCostPrice, sellingPrice, setSellingPrice, mrp, setMrp, barcode, setBarcode, rollNumber,
  setRollNumber, freightCost, setFreightCost, otherCost, setOtherCost, landedCost, setLandedCost, margin, setMargin, brand, setBrand,
  mfgBatchNumber, setMfgBatchNumber, mfgDate, setMfgDate, expiryDate, setExpiryDate, setShowLess, showLess, setFormSubmitted,
  setInventoryLocation, inventoryLocation, destDepartmentId, user, containerClassName, BatchSelectorForFG, disablePriceChange, expiryDateFormat, manufacturingDateFormat, priceMasking, batchCf, setBatchCf,
}) => {
  const batchConfig = org?.organisation?.[0]?.inventory_config?.settings?.batch_management;
  let showBatchFields = false;
  const { isDataMaskingPolicyEnable, isHideCostPrice, isHideSellingPrice } = priceMasking;

  function isAnyFieldEnabled(config, fields) {
    // Iterate over the array of fields
    for (let i = 0; i < fields.length; i++) {
      const field = fields[i];
      // Check if the current field is enabled in the config
      if (config?.[`${field}_is_mandatory`]) {
        // Return true as soon as one enabled field is found
        return true;
      }
    }
    // Return false if no fields are enabled
    return false;
  }

  showBatchFields = isAnyFieldEnabled(batchConfig, ['cost_price', 'lot_number', 'selling_price', 'mrp', 'ar_number', 'roll_no', 'brand', 'mfg_batch_no', 'manufacturing_date', 'expiry_date']) ? isAnyFieldEnabled(batchConfig, ['cost_price', 'lot_number', 'selling_price', 'mrp', 'ar_number', 'roll_no', 'brand', 'mfg_batch_no', 'manufacturing_date', 'expiry_date']) && !showLess : showLess;

  const enablesWarehouseLocations = user?.tenant_info?.inventory_config?.settings?.enable_location_based_stock;
  const isWarehouseLocationMandatory = user?.tenant_info?.inventory_config?.settings?.is_inventory_location_mandatory;
  const inputClassName = (field, isMandatory) => `orgFormInput fg-batch-input ${(formSubmitted && !field && isMandatory) ? 'fg-batch-input-error' : ''} `;

  // array for configuration input fields
  const inputFields = [
    {
      title: "Batch #",
      renderCase: true,
      mandatory: true,
      render: (
        <div className={`create-batch-table__input-wrapper ${isBulkUploaded ? 'disabled-element' : ''}`}>
          <H3Text
            text="Batch #"
            className="create-batch-table__input-label"
            required
          />
          &nbsp;
          <H3FormInput
            value={batchNumber}
            onChange={(event) => setBatchNumber(event?.target?.value)}
            type="text"
            disabled={disabled || isBatchNumberEditable}
            inputClassName={inputClassName(batchNumber, true)}
            fixShowErrorCss
            containerClassName={containerClassName}
          />
        </div>
      ),
    },
    {
      title: "Shelf/Rack",
      renderCase: enablesWarehouseLocations,
      mandatory: isWarehouseLocationMandatory,
      render: (
        <div className="create-batch-table__input-wrapper">
          <H3Text
            text="Shelf/Rack"
            className="create-batch-table__input-label"
            required={isWarehouseLocationMandatory}
          />
          &nbsp;
          <WarehouseLocationSelector
            inputClassName={inputClassName(inventoryLocation, isWarehouseLocationMandatory)}
            hideTitle
            selectedInventoryLocation={inventoryLocation}
            onChange={(id, name) => {
              setInventoryLocation(id);
            }}
            destDepartmentId={destDepartmentId}
            showError={formSubmitted && !inventoryLocation}
            containerClassName={containerClassName}
          />
        </div>
      ),
    },
    {
      title: "Cost Price",
      renderCase: batchConfig?.cost_price_is_enabled,
      mandatory: batchConfig?.cost_price_is_mandatory,
      render: (
        <div className={`create-batch-table__input-wrapper ${isBulkUploaded ? 'disabled-element' : ''}`}>
          <H3Text
            text="Cost Price"
            className="create-batch-table__input-label"
            required={batchConfig?.cost_price_is_mandatory}
          />
          &nbsp;
          <H3FormInput
            value={fgBatchCostPrice}
            onChange={(event) => setFgBatchCostPrice(event.target.value)}
            type="number"
            disabled={disabled || disablePriceChange}
            inputClassName={inputClassName(fgBatchCostPrice, batchConfig?.cost_price_is_mandatory)}
            containerClassName={containerClassName}
            hideInput={isDataMaskingPolicyEnable && isHideCostPrice}
            popOverMessage={"You don't have access to view cost price"}
          />
        </div>
      ),
    },
    {
      title: 'Selling Price',
      renderCase: batchConfig?.selling_price_is_enabled,
      mandatory: batchConfig?.selling_price_is_mandatory,
      render: (
        <div className={`create-batch-table__input-wrapper ${isBulkUploaded ? 'disabled-element' : ''}`}>
          <H3Text
            text="Selling Price"
            className="create-batch-table__input-label"
            required={batchConfig?.selling_price_is_mandatory}
          />
          &nbsp;
          <H3FormInput
            value={sellingPrice}
            onChange={(event) => setSellingPrice(event.target.value)}
            type="number"
            disabled={disabled}
            inputClassName={inputClassName(sellingPrice, batchConfig?.selling_price_is_mandatory)}
            containerClassName={containerClassName}
            hideInput={isDataMaskingPolicyEnable && isHideSellingPrice}
            popOverMessage={"You don't have access to view selling price"}
          />
        </div>
      ),
    },
    {
      title: "MRP",
      renderCase: batchConfig?.mrp_is_enabled,
      mandatory: batchConfig?.mrp_is_mandatory,
      render: (
        <div className={`create-batch-table__input-wrapper ${isBulkUploaded ? 'disabled-element' : ''}`}>
          <H3Text
            text="MRP"
            className="create-batch-table__input-label"
            required={batchConfig?.mrp_is_mandatory}
          />
          &nbsp;
          <H3FormInput
            value={mrp}
            onChange={(event) => setMrp(event.target.value)}
            type="number"
            disabled={disabled}
            inputClassName={inputClassName(mrp, batchConfig?.mrp_is_mandatory)}
            containerClassName={containerClassName}
          />
        </div>
      ),
    },
    {
      title: "Lot #",
      renderCase: batchConfig?.lot_number_is_enabled,
      mandatory: batchConfig?.lot_number_is_mandatory,
      render: (
        <div className={`create-batch-table__input-wrapper ${isBulkUploaded ? 'disabled-element' : ''}`}>
          <H3Text
            text="Lot #"
            className="create-batch-table__input-label"
            required={batchConfig?.lot_number_is_mandatory}
          />
          &nbsp;
          <div style={{ width: '120px' }}>
            <H3FormInput
              value={lotNumber}
              onChange={(event) => setLotNumber(event?.target?.value)}
              type="text"
              disabled={disabled}
              inputClassName={inputClassName(lotNumber, batchConfig?.lot_number_is_mandatory)}
              containerClassName={containerClassName}
            />
          </div>
        </div>
      ),
    },
    {
      title: "AR Number",
      renderCase: batchConfig?.ar_number_is_enabled,
      mandatory: batchConfig?.ar_number_is_mandatory,
      render: (
        <div className={`create-batch-table__input-wrapper ${isBulkUploaded ? 'disabled-element' : ''}`}>
          <H3Text
            text="AR Number"
            className="create-batch-table__input-label"
            required={batchConfig?.ar_number_is_mandatory}

          />
          &nbsp;
          <H3FormInput
            value={arNumber}
            onChange={(event) => setArNumber(event.target.value)}
            type="text"
            disabled={disabled}
            inputClassName={inputClassName(arNumber, batchConfig?.ar_number_is_mandatory)}
            containerClassName={containerClassName}
          />
        </div>
      ),
    },
    {
      title: "Roll No",
      renderCase: batchConfig?.roll_no_is_enabled,
      mandatory: batchConfig?.roll_no_is_mandatory,
      render: (
        <div className={`create-batch-table__input-wrapper ${isBulkUploaded ? 'disabled-element' : ''}`}>
          <H3Text
            text="Roll No"
            className="create-batch-table__input-label"
            required={batchConfig?.roll_no_is_mandatory}
          />
          &nbsp;
          <H3FormInput
            value={rollNumber}
            onChange={(event) => setRollNumber(event.target.value)}
            type="number"
            disabled={disabled}
            inputClassName={inputClassName(rollNumber, batchConfig?.roll_no_is_mandatory)}
            containerClassName={containerClassName}
          />
        </div>
      ),
    },
    {
      title: "Brand",
      renderCase: batchConfig?.brand_is_enabled,
      mandatory: batchConfig?.brand_is_mandatory,
      render: (
        <div className={`create-batch-table__input-wrapper ${isBulkUploaded ? 'disabled-element' : ''}`}>
          <H3Text
            text="Brand"
            className="create-batch-table__input-label"
            required={batchConfig?.brand_is_mandatory}
          />
          &nbsp;
          <H3FormInput
            value={brand}
            onChange={(event) => setBrand(event.target.value)}
            type="text"
            disabled={disabled}
            inputClassName={inputClassName(brand, batchConfig?.brand_is_mandatory)}
            containerClassName={containerClassName}
          />
        </div>
      ),
    },
    {
      title: "Mfg Batch#",
      renderCase: batchConfig?.mfg_batch_no_is_enabled,
      mandatory: batchConfig?.mfg_batch_no_is_mandatory,
      render: (
        <div className={`create-batch-table__input-wrapper ${isBulkUploaded ? 'disabled-element' : ''}`}>
          <H3Text
            text="Mfg Batch#"
            className="create-batch-table__input-label"
            required={batchConfig?.mfg_batch_no_is_mandatory}
          />
          &nbsp;&nbsp;
          <H3FormInput
            value={mfgBatchNumber}
            onChange={(event) => setMfgBatchNumber(event.target.value)}
            type="text"
            disabled={disabled}
            inputClassName={inputClassName(mfgBatchNumber, batchConfig?.mfg_batch_no_is_mandatory)}
            containerClassName={containerClassName}
          />
        </div>
      ),
    },
    {
      title: "Expiry",
      renderCase: true,
      mandatory: batchConfig?.expiry_date_is_mandatory,
      render: (
        <div className={`create-batch-table__input-wrapper ${isBulkUploaded ? 'disabled-element' : ''}`}>
          <H3Text
            text="Expiry"
            className="create-batch-table__input-label"
            required={batchConfig?.expiry_date_is_mandatory}
          />
          &nbsp; &nbsp;
          <div className={(!expiryDate && batchConfig?.expiry_date_is_mandatory && formSubmitted) ? `${containerClassName} fg-batch-picker-error` : `${containerClassName}`}>
            {dayjs(expiryDate).format('YYYY-MM-DD') !== INFINITE_EXPIRY_DATE
              ? (
                <DatePicker
                  value={expiryDate}
                  onChange={(value) => setExpiryDate(value)}
                  disabled={disabled}
                  style={{
                    height: '32px',
                  }}
                  format={expiryDateFormat}
                  picker={expiryDateFormat !== 'DD/MM/YYYY' ? 'month' : undefined}
                  disabledDate={(current) => current.isBefore(dayjs().subtract(1, 'day'))}
                />
              ) : (
                <div>No Expiry</div>
              )}
          </div>
        </div>
      ),
    },
    {
      title: "Mfg Date",
      renderCase: batchConfig?.manufacturing_date_is_enabled,
      mandatory: batchConfig?.manufacturing_date_is_mandatory,
      render: (
        <div className={`create-batch-table__input-wrapper ${isBulkUploaded ? 'disabled-element' : ''}`}>
          <H3Text
            text="Mfg Date"
            className="create-batch-table__input-label"
            required={batchConfig?.manufacturing_date_is_mandatory}
          />
          &nbsp; &nbsp;
          <div className={(!mfgDate && batchConfig?.manufacturing_date_is_mandatory && formSubmitted) ? `${containerClassName} fg-batch-picker-error` : `${containerClassName}`}>
            <DatePicker
              value={mfgDate}
              onChange={(value) => setMfgDate(value)}
              disabled={disabled}
              style={{
                height: '30px',
                width: '150px',
                borderRadius: '3px',
                border: '1px dashed rgb(218, 218, 218) !important;',
              }}
              format={manufacturingDateFormat}
              picker={manufacturingDateFormat !== 'DD/MM/YYYY' ? 'month' : ''}
            />
          </div>
        </div>
      )
    },
  ]

  // function to include
  const inputCustomFields = () => {

    batchCf?.map((item) => {

      inputFields.push({
        title: item?.fieldName,
        renderCase: item?.isActive,
        mandatory: item?.isRequired,
        render: (

          <div className="create-batch-table__input-wrapper">

            <div style={{ width: '80px', display: 'flex', alignItems: 'center', }}>
              <H3Text
                text={item?.fieldName}
                className="create-batch-table__input-label"
                required={item?.isRequired}
              />
              &nbsp;&nbsp;
            </div>
            <div style={{ display: 'flex', alignItems: 'center', paddingTop: '12px', paddingLeft: '3px', }}>
              <CustomFieldLine
                customFields={batchCf}
                cfId={item?.cfId}
                customLineInputChange={value => setBatchCf(value)}
                batchClass
              />
            </div>
            {/* {item?.fieldType?.toUpperCase() === 'TEXT' && (
              <H3FormInput 
                value={item?.fieldValue}
                onChange={(value) => ()}
                type='text'
                inputClassName=''
                containerClassName={containerClassName}
              />
            )}
            {item?.fieldType?.toUppperCase() === 'NUMBERS' && (
              <H3FormInput
                value={item?.fieldValue}
                onChange={(value) => ()}
                type='number'
                inputClassName=''
                containerClassName={containerClassName}
              />
            )}
            {item?.fieldType?.toUpperCase() == 'DROPDOWN' && (
              <div>
                <Select
                  bordered={false}
                  value={item?.fieldValue}
                  filterOption={false}
                  showSearch
                  className=''
                  onChange={(value) => ()}
                >
                  {item?.defaultData?.map((option) => (
                    <Option
                      key={option}
                      title={option}
                      value={option}
                    >
                      {option}
                    </Option>
                  ))}
                </Select>
              </div>
            )}
            {item?.fieldType?.toUpperCase() === 'ATTACHMENT' && (
              <div>
                <Upload></Upload>
              </div>
            )} */}

          </div>
        )
      })
    })
  }

  // function for fields that will be rendered
  const renderFields = () => {

    inputCustomFields();

    const fields = [];

    // render mandatory fields at the top
    inputFields?.filter((item) => (item?.mandatory && item?.renderCase))?.map((item) => {
      fields.push(item);
    })
    // render non-mandatory fields next
    inputFields?.filter((item) => !(item?.mandatory && item?.renderCase))?.map((item) => {
      fields.push(item);
    })

    return !showLess ? fields : fields?.slice(0, 4);
  }

  return (
    <Fragment>
      <Fragment>
        <div
          style={{
            width: '240px',
          }}
          className="create-batch-table__batch-input"
        >
          {renderFields()?.map((item) => (item?.renderCase && item?.render))}
          <div
            onClick={() => {
              setShowLess(!showLess);
            }}
            className="more-batch-info"
            style={{
              display: 'flex',
              alignItems: 'center',
              width: '100px',
            }}
          >
            <div>
              {`show ${!showLess ? 'less' : 'more'}`}
            </div>
            &nbsp;
            {!showLess ? <FontAwesomeIcon icon={faChevronUp} /> : <FontAwesomeIcon icon={faChevronDown} />}
          </div>
        </div>
      </Fragment>
    </Fragment>
  );
};

const mapStateToProps = ({
  UserReducers,
}) => ({
  user: UserReducers.user,
  MONEY: UserReducers.MONEY,
  org: UserReducers.org,
  priceMasking: UserReducers.priceMasking,
});

BatchSelectorForFG.propTypes = {
  formSubmitted: PropTypes.bool,
  org: PropTypes.object,
  isBulkUploaded: PropTypes.bool,
  batchNumber: PropTypes.string,
  setBatchNumber: PropTypes.func,
  disabled: PropTypes.bool,
  isBatchNumberEditable: PropTypes.bool,
  lotNumber: PropTypes.string,
  setLotNumber: PropTypes.func,
  setArNumber: PropTypes.func,
  arNumber: PropTypes.string,
  fgBatchCostPrice: PropTypes.string,
  setFgBatchCostPrice: PropTypes.func,
  sellingPrice: PropTypes.string,
  setSellingPrice: PropTypes.func,
  mrp: PropTypes.string,
  setMrp: PropTypes.func,
  barcode: PropTypes.string,
  setBarcode: PropTypes.func,
  rollNumber: PropTypes.string,
  setRollNumber: PropTypes.func,
  freightCost: PropTypes.string,
  setFreightCost: PropTypes.func,
  otherCost: PropTypes.string,
  setOtherCost: PropTypes.func,
  landedCost: PropTypes.string,
  setLandedCost: PropTypes.func,
  margin: PropTypes.string,
  setMargin: PropTypes.func,
  brand: PropTypes.string,
  setBrand: PropTypes.func,
  mfgBatchNumber: PropTypes.string,
  setMfgBatchNumber: PropTypes.func,
  mfgDate: PropTypes.string,
  setMfgDate: PropTypes.func,
  expiryDate: PropTypes.string,
  setExpiryDate: PropTypes.func,
  setShowLess: PropTypes.func,
  showLess: PropTypes.bool,

};

export default connect(mapStateToProps)(withRouter(BatchSelectorForFG));
